import { Grid, Card, CardHeader, CardContent, Button, Typography, Checkbox } from '@mui/material';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { contactApiKeyStore } from '../../../api/contact-api-key-store';
import { useEffect, useState } from 'react';
import { ContactsDialog } from '../../contacts/contacts.dialog';
import { Contact } from '../../../models/contact';
import { useFormContext } from 'react-hook-form';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { ContactsDialogWithGridSelect } from '../../contacts/contact-diallog-with-grid-selection';
import { columns } from '../../contacts/grid-columns';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { fastFieldContactColumn } from '../../contacts/fast-field-contact-column';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
  headingStyle: { fontWeight: 'bold' },
};

interface CellProps {
  moduleData: any;
}

interface CustomGridCellProps {
  field: string;
}

interface ContactsFormProps {
  name: string;
  label: string;
  projectID: number;
  updateContactList: (unitContacts: any) => void;
}

export const ContactsForm = ({ name, label, projectID, updateContactList }: ContactsFormProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { setValue, getValues } = useFormContext();
  const [valueForTab, setValueForTab] = useState('view');
  const [updatedProjectContactData, setProjectContactData] = useState<any[]>();
  const [editItem, setEditItem] = useState<Contact>();

  useEffect(() => {
    if (getValues(name)) {
      const updatedData = getValues(name)?.map((d: any) => ({
        ...d,
        isFastFieldCM: d?.isFastFieldCM,
      }));
      setProjectContactData(updatedData);
    }
  }, [getValues(name)]);

  const handleOpenAddContactForm = () => {
    setOpenDialog(true);
  };

  const handleCloseAddContactForm = (contacts: Contact[]) => {
    setOpenDialog(false);
    setValue(name, contacts);
  };

  const FastFieldCell = ({ moduleData }: CellProps) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedData = updatedProjectContactData?.map((d: any) => ({
        ...d,
        isFastFieldCM: moduleData?.contactsID == d?.contactsID ? event.target.checked : false,
      }));
      setProjectContactData(updatedData);
      updateContactList(updatedData);
    };

    return (
      <td>
        <Checkbox
          checked={
            updatedProjectContactData?.find((pl: any) => pl?.contactsID == moduleData?.contactsID)?.isFastFieldCM
          }
          onChange={handleChange}
          inputProps={{ 'aria-label': 'controlled' }}
          disabled
        />
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'isFastFieldCM') {
      return (dataItem: any) => {
        return <FastFieldCell moduleData={dataItem.dataItem} />;
      };
    }
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('UnitEditContact', 'UnitEditContact'));

  // const { isLoading, data: Offices, refetch } = useLoadData<ApiResponse<Contact[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredUnitEditContacts, setFilteredUnitEditContacts] = useState<Contact[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredUnitEditContactList = (getValues(name) || []).filter((unitEditContact: any) =>
      Object.values(unitEditContact).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredUnitEditContacts(filteredUnitEditContactList || []);
  }, [debouncedSearchTerm, getValues(name)]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'UnitEditContact',
      pageName: 'UnitEditContact',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'UnitEditContact',
      pageName: 'UnitEditContact',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            CONTACTS
          </Typography>
        }
        action={
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
            <Button
              className="btnLink"
              sx={{ p: 0, m: 0, mr: 1 }}
              variant="text"
              onClick={() => handleOpenAddContactForm()}
            >
              Map Contacts
            </Button>
          </GuardedComponent>
        }
      ></CardHeader>
      <CardContent>
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            {/* <FormAutoCompleteMultiSelectDynamic
              name={name}
              label={label}
              dataItems={getValues(name)}
              getQueryKeyConfig={() => contactApiKeyStore.getProjectContacts(projectID)}
              optionsLabelKey="contactsFirstName"
              optionsValueKey="contactsID"
            /> */}

            <SiTrackGrid
              fileTitle='unit_contact'
              dataItemKey="contactsID"
              columns={fastFieldContactColumn}
              customCell={(field) =>
                CustomGridCell({
                  field,
                })
              }
              actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              isBusy={isLoadingGridLayout}
              // -----------------Grid Layout-------
              dataItems={filteredUnitEditContacts || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            // -----------------Grid Layout-------
            />
          </Grid>
        </Grid>
        {/* {openDialog && (
          <ContactsDialog
            open={openDialog}
            onClose={handleCloseAddContactForm}
            contacts={getValues(name)}
            customerID={getValues('customer.customerID')}
          />
        )} */}
        {openDialog && (
          <ContactsDialogWithGridSelect
            open={openDialog}
            onClose={handleCloseAddContactForm}
            contacts={getValues(name)}
            customerID={getValues('customer.customerID')}
            setValue={setValueForTab}
            value={valueForTab}
            editItem={editItem}
            setEditItem={setEditItem}
            watchKeys={['office.officeID']}
            getQueryKeyConfig={() => contactApiKeyStore.getProjectContacts(projectID)}
            page="unit"
          />
        )}
      </CardContent>
    </Card>
  );
};
